/**
 * 轻量 i18n —— 与小程序 locales 同源，不引入 vue-i18n。
 *
 * 用法：
 *   1) Vue 模板：
 *        import { t, currentLocale } from '@/plugins/utils/i18n';
 *        {{ t('summary') }}
 *      或（setup 函数内）：
 *        const { t, L, currentLocale } = useI18n();
 *        {{ t('summary') }}   /   {{ L.summary }}
 *
 *   2) 图表 / 普通 JS 模块：
 *        import { L } from '@/plugins/utils/i18n';
 *        L.jan   // 动态：当前语言的 "一月" / "Jan"
 *
 *   3) 切换语言：
 *        import { setLocale } from '@/plugins/utils/i18n';
 *        setLocale('en');   // 写入 localStorage 并 reload
 *
 *   L 是 Proxy，每次访问都读当前 currentLocale；
 *   但已有图表/模块顶层的 `const monthNameArray = [L.jan, ...]` 在模块加载时就求值了，
 *   切换语言依赖 location.reload() 触发重新加载（业界主流做法）。
 */
import { ref, computed } from 'vue';
import { locales } from './locales.js';

const STORAGE_KEY = 'wuji_locale';
const SUPPORTED = ['zh-Hans', 'en'];
const DEFAULT_LOCALE = 'zh-Hans';

function readInitialLocale() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED.includes(saved)) return saved;
  } catch (e) { /* ignore */ }
  // 可选：首次访问按浏览器语言推断
  try {
    const lang = (navigator.language || '').toLowerCase();
    if (lang.startsWith('zh')) return 'zh-Hans';
    if (lang.startsWith('en')) return 'en';
  } catch (e) { /* ignore */ }
  return DEFAULT_LOCALE;
}

/** 响应式当前语言 */
export const currentLocale = ref(readInitialLocale());

/** 是否英文（供模板里 v-if 判断用） */
export const isEn = computed(() => currentLocale.value === 'en');

/**
 * 读文案：t(key) → 当前语言下的翻译；找不到时 fallback 到中文再 fallback 到 key 本身。
 *   t('summary')                     // '摘要'
 *   t('nonexist_key')                // 'nonexist_key'
 *   t('energy_phases_detail')        // 子对象（适合需要子结构的场景）
 */
export function t(key) {
  const dict = locales[currentLocale.value] || locales[DEFAULT_LOCALE];
  if (key in dict) return dict[key];
  const fallback = locales[DEFAULT_LOCALE];
  if (key in fallback) return fallback[key];
  return key;
}

/**
 * L 是 Proxy，`L.xxx` 等价 `t('xxx')`；用于兼容图表里原来的 `L.jan` 写法。
 * 注意：对象解构 `const { jan } = L` 会绑死为当前值，请用 `L.jan` 按需访问。
 */
export const L = new Proxy({}, {
  get(_target, prop) {
    if (prop === '__v_isRef' || typeof prop === 'symbol') return undefined;
    return t(prop);
  },
  has(_target, prop) {
    const dict = locales[currentLocale.value] || locales[DEFAULT_LOCALE];
    return prop in dict;
  },
});

/**
 * 切换语言（写 localStorage + reload）。
 * 之所以 reload：chart 模块顶层的常量（monthNameArray 等）已在模块加载时求值绑死。
 */
export function setLocale(loc) {
  if (!SUPPORTED.includes(loc)) {
    console.warn('[i18n] unsupported locale:', loc);
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, loc);
  } catch (e) { /* ignore */ }
  currentLocale.value = loc;
  // 整页刷新以确保所有已加载模块的顶层常量重新求值
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
}

/** composable 风格（可选） */
export function useI18n() {
  return { t, L, currentLocale, isEn, setLocale };
}

/** 支持列表（给语言切换 UI 用） */
export const SUPPORTED_LOCALES = [
  { code: 'zh-Hans', label: '中文', shortLabel: '中' },
  { code: 'en',      label: 'English', shortLabel: 'EN' },
];
